import * as R from 'remeda';
import { getToolName, isToolUIPart } from 'ai';
import type { z } from 'zod';
import {
  type ChatUIMessage,
  subagentRunPartSchema,
  subagentStepPartSchema,
} from '../hooks/UseAgentChat/schemas';
import { isHiddenTool } from '../utils/message';
import { formatToolOutput, type ToolCallDisplay } from './toolCallUtils';

type Parts = ChatUIMessage['parts'];

export type SubagentRun = z.infer<typeof subagentRunPartSchema>;

export const isAgentToolName = (toolName: string) =>
  toolName.startsWith('agent-');

/**
 * Pairs each `agent-*` call with the subagent run Mastra streams for it. A run
 * shares no id with its call but starts after it, so a new run goes to the
 * earliest preceding call still without one — right for sequential and
 * parallel calls alike.
 */
export const getSubagentRuns = (parts: Parts): Map<string, SubagentRun> => {
  const runs = new Map<string, SubagentRun>();
  const callIdByRunId = new Map<string, string>();
  const waiting: string[] = [];

  parts.forEach(part => {
    if (isToolUIPart(part) && isAgentToolName(getToolName(part))) {
      waiting.push(part.toolCallId);
      return;
    }
    if (part.type !== 'data-tool-agent' || !part.id) return;
    // Pair before validating: a malformed run still takes its call's slot, so
    // later runs don't shift onto the wrong call.
    const callId = callIdByRunId.get(part.id) ?? waiting.shift();
    if (!callId) return;
    callIdByRunId.set(part.id, callId);

    const result = subagentRunPartSchema.safeParse(part);
    if (result.success) runs.set(callId, result.data);
  });

  return runs;
};

/**
 * Every step of a run seen so far. The snapshot holds only the current step;
 * completed steps arrive as step parts, and in the snapshot's `steps` once the
 * run finishes — so a call can appear twice.
 */
export const getSubagentSteps = (parts: Parts, run: SubagentRun) => [
  ...parts.flatMap(part => {
    if (part.type !== 'data-tool-agent-step') return [];
    const event = subagentStepPartSchema.safeParse(part).data?.data;
    return event?.runId === run.id ? [event.step] : [];
  }),
  ...run.data.steps,
  run.data,
];

/** The visible tool calls a subagent made, for the completed steps list. */
export const getSubagentToolCalls = (
  parts: Parts,
  run: SubagentRun
): ToolCallDisplay[] => {
  const steps = getSubagentSteps(parts, run);
  const results = new Map(
    steps.flatMap(step => step.toolResults).map(r => [r.toolCallId, r])
  );
  const errorIds = new Set(
    steps.flatMap(step => step.toolErrors).map(e => e.toolCallId)
  );

  return R.pipe(
    steps.flatMap(step => step.toolCalls),
    R.uniqueBy(call => call.toolCallId),
    R.filter(call => !isHiddenTool(call.toolName)),
    R.map(({ toolCallId, toolName, args }) => {
      const result = results.get(toolCallId);
      const isError = errorIds.has(toolCallId);
      return {
        toolCallId,
        toolName,
        args,
        result:
          result?.result === undefined
            ? undefined
            : formatToolOutput(result.result),
        state: isError
          ? 'output-error'
          : result
            ? 'output-available'
            : 'input-available',
        isError,
      };
    })
  );
};
